import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Database, Search, Image, Save, Upload, Loader2, Check, X, ExternalLink, Edit2, RefreshCw, Copy, CheckCircle, Clipboard, ArrowUpDown, ArrowUp, ArrowDown, Download, FileSpreadsheet, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { capitalizeFirst } from '../lib/utils';

export const BazaDateProduse = () => {
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [searchingId, setSearchingId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editingNameId, setEditingNameId] = useState(null);
    const [editDescription, setEditDescription] = useState('');
    const [editName, setEditName] = useState('');
    const fileInputRef = useRef(null);
    const [uploadingId, setUploadingId] = useState(null);
    const scrollContainerRef = useRef(null);
    
    // Search state - persist in sessionStorage
    const [search, setSearch] = useState(() => {
        return sessionStorage.getItem('baza_date_search') || '';
    });
    
    // Save search to sessionStorage
    useEffect(() => {
        sessionStorage.setItem('baza_date_search', search);
    }, [search]);
    
    // Sorting state - persist in sessionStorage
    const [sortBy, setSortBy] = useState(() => {
        return sessionStorage.getItem('baza_date_sortBy') || 'name';
    });
    const [sortDir, setSortDir] = useState(() => {
        return sessionStorage.getItem('baza_date_sortDir') || 'asc';
    });
    
    // Save sort to sessionStorage
    useEffect(() => {
        sessionStorage.setItem('baza_date_sortBy', sortBy);
        sessionStorage.setItem('baza_date_sortDir', sortDir);
    }, [sortBy, sortDir]);
    
    // Lightbox state
    const [lightboxImage, setLightboxImage] = useState(null);
    
    // Export states
    const [exportingAll, setExportingAll] = useState(false);
    const [exportingNew, setExportingNew] = useState(false);
    const [duplicates, setDuplicates] = useState(null);
    const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
    const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
    
    // Track searched barcodes (persist in localStorage)
    const [searchedBarcodes, setSearchedBarcodes] = useState(() => {
        const saved = localStorage.getItem('searched_barcodes');
        return saved ? JSON.parse(saved) : [];
    });
    
    // Save searched barcodes to localStorage
    useEffect(() => {
        localStorage.setItem('searched_barcodes', JSON.stringify(searchedBarcodes));
    }, [searchedBarcodes]);
    
    // Export all products
    const exportAllProducts = async () => {
        setExportingAll(true);
        try {
            const response = await api.get('/products/v2/export/excel/all', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Produse_Toate_${new Date().toLocaleDateString('ro-RO').replace(/\./g, '-')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Excel exportat cu succes');
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.detail || 'Eroare la export');
        }
        setExportingAll(false);
    };
    
    // Export only new products (since last export)
    const exportNewProducts = async () => {
        setExportingNew(true);
        try {
            const response = await api.get('/products/v2/export/excel/new', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Produse_Noi_${new Date().toLocaleDateString('ro-RO').replace(/\./g, '-')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Excel cu produse noi exportat');
        } catch (err) {
            console.error('Export error:', err);
            if (err.response?.status === 404) {
                toast.info('Nu există produse noi de la ultimul export');
            } else {
                toast.error(err.response?.data?.detail || 'Eroare la export');
            }
        }
        setExportingNew(false);
    };

    // Check for duplicates
    const checkDuplicates = async () => {
        try {
            const res = await api.get('/products/v2/duplicates');
            setDuplicates(res.data);
            setShowDuplicatesModal(true);
        } catch (err) {
            toast.error('Eroare la verificarea duplicatelor');
        }
    };

    // Clean duplicates
    const cleanDuplicates = async () => {
        setCleaningDuplicates(true);
        try {
            const res = await api.post('/products/v2/duplicates/clean');
            toast.success(res.data.message);
            setShowDuplicatesModal(false);
            setDuplicates(null);
            loadProducts();
        } catch (err) {
            toast.error('Eroare la curățarea duplicatelor');
        }
        setCleaningDuplicates(false);
    };
    
    // Save and restore scroll position
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        
        // Restore scroll position after products load
        const savedScroll = sessionStorage.getItem('baza_date_scroll');
        if (savedScroll && !loading) {
            container.scrollTop = parseInt(savedScroll) || 0;
        }
        
        // Save scroll position on scroll
        const handleScroll = () => {
            sessionStorage.setItem('baza_date_scroll', container.scrollTop.toString());
        };
        
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [loading]);

    const loadProducts = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            // Get ALL products from products_v2 database + store quantities
            const [productsRes, storeRes] = await Promise.all([
                api.get('/products/v2'),
                api.get('/store/v2')
            ]);
            
            // Build stock map from store
            const stockMap = new Map();
            storeRes.data.forEach(s => {
                stockMap.set(s.barcode, (stockMap.get(s.barcode) || 0) + (s.quantity || 0));
            });
            
            // Map products with their stock and metadata
            const allProducts = productsRes.data
                .filter(p => p.barcode && 
                    p.barcode !== 'TAXA_TRANSPORT' && 
                    p.barcode !== 'TRANSPORT' &&
                    !p.name?.toLowerCase().includes('transport'))
                .map(p => ({
                    barcode: p.barcode,
                    name: p.name,
                    category: p.category,
                    quantity: stockMap.get(p.barcode) || 0,
                    image_url: p.image_url || null,
                    product_description: p.product_description || null
                }))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            
            setProducts(allProducts);
        } catch (err) {
            toast.error('Eroare la încărcarea produselor');
            console.error(err);
        }
        if (!silent) setLoading(false);
    }, []);

    useEffect(() => {
        loadProducts();
        
        // Silent refresh when window gets focus (no loading state, keeps scroll)
        const handleFocus = () => {
            loadProducts(true);
        };
        
        window.addEventListener('focus', handleFocus);
        
        // Listen for product updates from other pages
        const handleProductUpdate = () => {
            loadProducts(true);
        };
        window.addEventListener('products-updated', handleProductUpdate);
        
        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('products-updated', handleProductUpdate);
        };
    }, [loadProducts]);
    
    // Toggle sort
    const toggleSort = (column) => {
        if (sortBy === column) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortDir('asc');
        }
    };
    
    // Get sort icon
    const getSortIcon = (column) => {
        if (sortBy !== column) return <ArrowUpDown size={12} className="text-zinc-400" />;
        return sortDir === 'asc' 
            ? <ArrowUp size={12} className="text-violet-600" /> 
            : <ArrowDown size={12} className="text-violet-600" />;
    };
    
    // Filter and sort products
    const filteredAndSortedProducts = useMemo(() => {
        // First filter
        let result = products.filter(p => {
            if (!search) return true;
            const searchLower = search.toLowerCase();
            return (
                p.name?.toLowerCase().includes(searchLower) ||
                p.barcode?.toLowerCase().includes(searchLower) ||
                p.product_description?.toLowerCase().includes(searchLower) ||
                p.category?.toLowerCase().includes(searchLower)
            );
        });
        
        // Then sort
        result.sort((a, b) => {
            let aVal, bVal;
            
            switch (sortBy) {
                case 'barcode':
                    aVal = a.barcode || '';
                    bVal = b.barcode || '';
                    break;
                case 'name':
                    aVal = (a.name || '').toLowerCase();
                    bVal = (b.name || '').toLowerCase();
                    break;
                case 'description':
                    aVal = (a.product_description || '').toLowerCase();
                    bVal = (b.product_description || '').toLowerCase();
                    break;
                case 'category':
                    aVal = (a.category || '').toLowerCase();
                    bVal = (b.category || '').toLowerCase();
                    break;
                case 'quantity':
                    aVal = a.quantity || 0;
                    bVal = b.quantity || 0;
                    break;
                case 'image':
                    aVal = a.image_url ? 1 : 0;
                    bVal = b.image_url ? 1 : 0;
                    break;
                default:
                    aVal = (a.name || '').toLowerCase();
                    bVal = (b.name || '').toLowerCase();
            }
            
            if (typeof aVal === 'number') {
                return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
            }
            return sortDir === 'asc' 
                ? aVal.localeCompare(bVal)
                : bVal.localeCompare(aVal);
        });
        
        return result;
    }, [products, search, sortBy, sortDir]);

    const searchProduct = async (product) => {
        setSearchingId(product.barcode);
        
        try {
            const res = await api.get(`/barcode/lookup/${product.barcode}`);
            
            // Mark as searched
            if (!searchedBarcodes.includes(product.barcode)) {
                setSearchedBarcodes(prev => [...prev, product.barcode]);
            }
            
            if (res.data.found) {
                // Save found data
                await api.post('/products/v2/update-metadata', {
                    barcode: product.barcode,
                    image_url: res.data.image_url,
                    product_description: res.data.description || res.data.name
                });
                
                // Update local state
                setProducts(prev => prev.map(p => 
                    p.barcode === product.barcode 
                        ? { ...p, image_url: res.data.image_url, product_description: res.data.description || res.data.name }
                        : p
                ));
                
                toast.success(`Găsit: ${res.data.name} (${res.data.response_time_ms}ms)`);
            } else {
                toast.warning(`Negăsit în ${res.data.response_time_ms}ms - Adaugă manual imaginea`);
            }
        } catch (err) {
            toast.error('Eroare la căutare');
            console.error(err);
        }
        
        setSearchingId(null);
    };

    const handleImageUpload = async (e, barcode) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        setUploadingId(barcode);
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('barcode', barcode);
        
        try {
            const res = await api.post('/products/v2/upload-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (res.data?.image_url) {
                setProducts(prev => prev.map(p => 
                    p.barcode === barcode 
                        ? { ...p, image_url: res.data.image_url }
                        : p
                ));
                toast.success('Imagine salvată!');
            }
        } catch (err) {
            toast.error('Eroare la încărcarea imaginii');
            console.error(err);
        }
        
        setUploadingId(null);
    };

    // Paste image from clipboard
    const handlePasteImage = async (barcode) => {
        try {
            const clipboardItems = await navigator.clipboard.read();
            let imageBlob = null;
            
            for (const item of clipboardItems) {
                // Check for image types
                const imageType = item.types.find(type => type.startsWith('image/'));
                if (imageType) {
                    imageBlob = await item.getType(imageType);
                    break;
                }
            }
            
            if (!imageBlob) {
                toast.error('Nu există imagine în clipboard');
                return;
            }
            
            setUploadingId(barcode);
            
            // Convert blob to file
            const file = new File([imageBlob], 'pasted-image.png', { type: imageBlob.type });
            
            const formData = new FormData();
            formData.append('file', file);
            formData.append('barcode', barcode);
            
            const res = await api.post('/products/v2/upload-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (res.data?.image_url) {
                setProducts(prev => prev.map(p => 
                    p.barcode === barcode 
                        ? { ...p, image_url: res.data.image_url }
                        : p
                ));
                toast.success('Imagine lipită și salvată!');
            }
        } catch (err) {
            if (err.name === 'NotAllowedError') {
                toast.error('Permite accesul la clipboard în browser');
            } else {
                toast.error('Eroare la lipirea imaginii');
                console.error(err);
            }
        }
        
        setUploadingId(null);
    };

    const startEditDescription = (product) => {
        setEditingId(product.barcode);
        setEditDescription(product.product_description || product.name || '');
    };

    const saveDescription = async (barcode) => {
        try {
            await api.post('/products/v2/update-metadata', {
                barcode: barcode,
                product_description: editDescription
            });
            
            setProducts(prev => prev.map(p => 
                p.barcode === barcode 
                    ? { ...p, product_description: editDescription }
                    : p
            ));
            
            toast.success('Descriere salvată!');
        } catch (err) {
            toast.error('Eroare la salvare');
        }
        
        setEditingId(null);
    };

    const startEditName = (product) => {
        setEditingNameId(product.barcode);
        setEditName(product.name || '');
    };

    const saveName = async (barcode) => {
        try {
            await api.post('/products/v2/update-name', {
                barcode: barcode,
                name: editName
            });
            
            setProducts(prev => prev.map(p => 
                p.barcode === barcode 
                    ? { ...p, name: editName }
                    : p
            ));
            
            toast.success('Nume salvat!');
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Eroare la salvare';
            toast.error(errorMsg);
        }
        
        setEditingNameId(null);
    };

    const copyDescriptionToName = async (product) => {
        if (!product.product_description) {
            toast.error('Nu există descriere de copiat');
            return;
        }
        
        try {
            await api.post('/products/v2/update-name', {
                barcode: product.barcode,
                name: product.product_description
            });
            
            setProducts(prev => prev.map(p => 
                p.barcode === product.barcode 
                    ? { ...p, name: product.product_description }
                    : p
            ));
            
            toast.success('Descriere copiată la nume!');
        } catch (err) {
            toast.error('Eroare la copiere');
        }
    };

    const getCompletionStats = () => {
        const total = products.length;
        const withImage = products.filter(p => p.image_url).length;
        const withDesc = products.filter(p => p.product_description).length;
        return { total, withImage, withDesc };
    };

    const stats = getCompletionStats();

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-zinc-900 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <Database size={32} className="text-violet-500" />
                        Bază de Date Produse
                    </h1>
                    <p className="text-zinc-500 mt-1">
                        Completează imaginile și descrierile produselor
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button 
                        onClick={checkDuplicates} 
                        variant="outline" 
                        className="gap-2 border-red-300 text-red-600 hover:bg-red-50"
                        data-testid="check-duplicates-btn"
                    >
                        <Search size={16} />
                        Verifică Duplicate
                    </Button>
                    <Button 
                        onClick={exportAllProducts} 
                        disabled={exportingAll}
                        variant="outline" 
                        className="gap-2 border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                        data-testid="export-all-products-btn"
                    >
                        <FileSpreadsheet size={16} className={exportingAll ? 'animate-pulse' : ''} />
                        {exportingAll ? 'Export...' : 'Export Toate'}
                    </Button>
                    <Button 
                        onClick={exportNewProducts} 
                        disabled={exportingNew}
                        variant="outline" 
                        className="gap-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                        data-testid="export-new-products-btn"
                    >
                        <Download size={16} className={exportingNew ? 'animate-bounce' : ''} />
                        {exportingNew ? 'Export...' : 'Export Noi'}
                    </Button>
                    <Button onClick={() => loadProducts()} variant="outline" className="gap-2">
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                        Reîncarcă
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-zinc-200 rounded-xl p-4">
                    <p className="text-sm text-zinc-500">Total Produse</p>
                    <p className="text-2xl font-bold text-zinc-900">{stats.total}</p>
                </div>
                <div className="bg-white border border-zinc-200 rounded-xl p-4">
                    <p className="text-sm text-zinc-500">Cu Imagine</p>
                    <p className="text-2xl font-bold text-emerald-600">{stats.withImage}</p>
                    <div className="w-full bg-zinc-200 rounded-full h-2 mt-2">
                        <div 
                            className="bg-emerald-500 h-2 rounded-full" 
                            style={{ width: `${stats.total ? (stats.withImage / stats.total * 100) : 0}%` }}
                        />
                    </div>
                </div>
                <div className="bg-white border border-zinc-200 rounded-xl p-4">
                    <p className="text-sm text-zinc-500">Cu Descriere</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.withDesc}</p>
                    <div className="w-full bg-zinc-200 rounded-full h-2 mt-2">
                        <div 
                            className="bg-blue-500 h-2 rounded-full" 
                            style={{ width: `${stats.total ? (stats.withDesc / stats.total * 100) : 0}%` }}
                        />
                    </div>
                </div>
            </div>
            
            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Caută produs, cod sau descriere..."
                    className="pl-10 pr-20"
                />
                {search && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        <span className="text-xs text-zinc-400">
                            {filteredAndSortedProducts.length} rezultate
                        </span>
                        <button 
                            onClick={() => setSearch('')}
                            className="text-zinc-400 hover:text-zinc-600"
                        >
                            <X size={14} />
                        </button>
                    </div>
                )}
            </div>

            {/* Products List */}
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                {/* Table Header with sorting */}
                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-xs font-medium text-zinc-500">
                    <button 
                        onClick={() => toggleSort('image')} 
                        className="col-span-1 flex items-center gap-1 hover:text-zinc-700"
                    >
                        Img {getSortIcon('image')}
                    </button>
                    <button 
                        onClick={() => toggleSort('barcode')} 
                        className="col-span-2 flex items-center gap-1 hover:text-zinc-700"
                    >
                        Cod de bare {getSortIcon('barcode')}
                    </button>
                    <button 
                        onClick={() => toggleSort('name')} 
                        className="col-span-3 flex items-center gap-1 hover:text-zinc-700"
                    >
                        Nume {getSortIcon('name')}
                    </button>
                    <button 
                        onClick={() => toggleSort('description')} 
                        className="col-span-2 flex items-center gap-1 hover:text-zinc-700"
                    >
                        Descriere {getSortIcon('description')}
                    </button>
                    <button 
                        onClick={() => toggleSort('quantity')} 
                        className="col-span-1 flex items-center gap-1 justify-center hover:text-zinc-700"
                    >
                        Stoc {getSortIcon('quantity')}
                    </button>
                    <div className="col-span-3 text-center">Acțiuni</div>
                </div>

                {/* Table Body */}
                <div ref={scrollContainerRef} className="divide-y divide-zinc-100 max-h-[600px] overflow-y-auto">
                    {filteredAndSortedProducts.map((product) => (
                        <div key={product.barcode} className="grid grid-cols-12 gap-2 px-4 py-2 items-center hover:bg-zinc-50">
                            {/* Image - clickable for lightbox */}
                            <div className="col-span-1">
                                {product.image_url ? (
                                    <div 
                                        className="w-10 h-10 rounded-lg border overflow-hidden bg-white cursor-pointer hover:ring-2 hover:ring-violet-400 transition-all"
                                        onClick={() => setLightboxImage({ url: product.image_url, name: product.name })}
                                    >
                                        <img 
                                            src={product.image_url} 
                                            alt={product.name}
                                            className="w-full h-full object-contain"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </div>
                                ) : (
                                    <div className="w-10 h-10 rounded-lg border-2 border-dashed border-zinc-300 flex items-center justify-center bg-zinc-50">
                                        <Image size={14} className="text-zinc-400" />
                                    </div>
                                )}
                            </div>

                            {/* Barcode */}
                            <div className="col-span-2">
                                <code className="text-xs bg-zinc-100 px-2 py-1 rounded font-mono">
                                    {product.barcode}
                                </code>
                            </div>

                            {/* Name - wider column */}
                            <div className="col-span-3">
                                {editingNameId === product.barcode ? (
                                    <div className="flex gap-1">
                                        <Input
                                            value={editName}
                                            onChange={(e) => setEditName(capitalizeFirst(e.target.value))}
                                            className="h-8 text-sm"
                                            autoFocus
                                        />
                                        <Button 
                                            size="sm" 
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-emerald-600"
                                            onClick={() => saveName(product.barcode)}
                                        >
                                            <Check size={14} />
                                        </Button>
                                        <Button 
                                            size="sm" 
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-red-500"
                                            onClick={() => setEditingNameId(null)}
                                        >
                                            <X size={14} />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1 group">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-zinc-900 text-sm truncate" title={product.name}>{product.name || '-'}</p>
                                            <p className="text-xs text-zinc-400">{product.category}</p>
                                        </div>
                                        <div className="flex gap-0.5 shrink-0">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                                                onClick={() => startEditName(product)}
                                                title="Editează nume"
                                            >
                                                <Edit2 size={12} />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-6 w-6 p-0 text-blue-500 opacity-0 group-hover:opacity-100"
                                                onClick={() => copyDescriptionToName(product)}
                                                title="Copiază descrierea la nume"
                                                disabled={!product.product_description}
                                            >
                                                <Copy size={10} />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Description - narrower column */}
                            <div className="col-span-2">
                                {editingId === product.barcode ? (
                                    <div className="flex gap-1">
                                        <Input
                                            value={editDescription}
                                            onChange={(e) => setEditDescription(capitalizeFirst(e.target.value))}
                                            className="h-8 text-sm"
                                            autoFocus
                                        />
                                        <Button 
                                            size="sm" 
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-emerald-600"
                                            onClick={() => saveDescription(product.barcode)}
                                        >
                                            <Check size={14} />
                                        </Button>
                                        <Button 
                                            size="sm" 
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-red-500"
                                            onClick={() => setEditingId(null)}
                                        >
                                            <X size={14} />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1 group">
                                        <p className="text-xs text-zinc-600 truncate flex-1" title={product.product_description}>
                                            {product.product_description || <span className="text-zinc-400 italic">-</span>}
                                        </p>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                                            onClick={() => startEditDescription(product)}
                                        >
                                            <Edit2 size={12} />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Stock */}
                            <div className="col-span-1 text-center">
                                <span className="font-semibold text-zinc-900">
                                    {product.quantity || 0}
                                </span>
                            </div>

                            {/* Actions - 3 columns */}
                            <div className="col-span-3 flex gap-1 justify-center">
                                {/* Search Button - Green if already searched */}
                                <Button
                                    size="sm"
                                    variant={searchedBarcodes.includes(product.barcode) ? "default" : "outline"}
                                    className={`h-7 text-xs ${searchedBarcodes.includes(product.barcode) ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : ''}`}
                                    onClick={() => searchProduct(product)}
                                    disabled={searchingId === product.barcode}
                                    title={searchedBarcodes.includes(product.barcode) ? 'Deja căutat' : 'Caută în baze de date externe'}
                                >
                                    {searchingId === product.barcode ? (
                                        <Loader2 size={12} className="animate-spin" />
                                    ) : searchedBarcodes.includes(product.barcode) ? (
                                        <CheckCircle size={12} />
                                    ) : (
                                        <Search size={12} />
                                    )}
                                </Button>

                                {/* Upload Image Button */}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7"
                                    onClick={() => {
                                        setUploadingId(product.barcode);
                                        document.getElementById(`file-${product.barcode}`)?.click();
                                    }}
                                    disabled={uploadingId === product.barcode}
                                    title="Încarcă imagine"
                                >
                                    {uploadingId === product.barcode ? (
                                        <Loader2 size={12} className="animate-spin" />
                                    ) : (
                                        <Upload size={12} />
                                    )}
                                </Button>
                                
                                {/* Paste Image Button */}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7"
                                    onClick={() => handlePasteImage(product.barcode)}
                                    disabled={uploadingId === product.barcode}
                                    title="Lipește imagine din clipboard"
                                >
                                    <Clipboard size={12} />
                                </Button>
                                
                                <input
                                    id={`file-${product.barcode}`}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleImageUpload(e, product.barcode)}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {filteredAndSortedProducts.length === 0 && (
                    <div className="p-8 text-center text-zinc-500">
                        {search ? 'Niciun produs găsit' : 'Nu există produse în stoc'}
                    </div>
                )}
            </div>
            
            {/* Image Lightbox - larger */}
            {lightboxImage && (
                <div 
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                    onClick={() => setLightboxImage(null)}
                >
                    <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl">
                        <button
                            onClick={() => setLightboxImage(null)}
                            className="absolute top-3 right-3 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white z-10"
                        >
                            <X size={20} />
                        </button>
                        <img 
                            src={lightboxImage.url} 
                            alt={lightboxImage.name}
                            className="max-w-full max-h-[85vh] object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <div className="p-4 bg-zinc-100 text-center">
                            <p className="font-medium text-zinc-900 text-lg">{lightboxImage.name}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Duplicates Modal */}
            <Dialog open={showDuplicatesModal} onOpenChange={setShowDuplicatesModal}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 size={20} />
                            Verificare Duplicate
                        </DialogTitle>
                    </DialogHeader>
                    
                    {duplicates && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div className="bg-zinc-100 p-3 rounded-lg">
                                    <p className="text-2xl font-bold">{duplicates.total_products}</p>
                                    <p className="text-sm text-zinc-500">Total Produse</p>
                                </div>
                                <div className="bg-zinc-100 p-3 rounded-lg">
                                    <p className="text-2xl font-bold">{duplicates.unique_barcodes}</p>
                                    <p className="text-sm text-zinc-500">Coduri Unice</p>
                                </div>
                                <div className="bg-red-100 p-3 rounded-lg">
                                    <p className="text-2xl font-bold text-red-600">{duplicates.duplicate_count}</p>
                                    <p className="text-sm text-red-500">Duplicate</p>
                                </div>
                            </div>

                            {duplicates.duplicate_count > 0 ? (
                                <>
                                    <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                                        {duplicates.duplicates.map((dup, i) => (
                                            <div key={i} className="p-3 flex justify-between items-center">
                                                <div>
                                                    <p className="font-medium">{dup.name}</p>
                                                    <p className="text-sm text-zinc-500">Cod: {dup.barcode}</p>
                                                </div>
                                                <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-sm font-medium">
                                                    {dup.count}x
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                        <p className="text-sm text-amber-700">
                                            <strong>Curățare:</strong> Se va păstra primul produs (cel mai vechi) pentru fiecare cod și se vor șterge duplicatele.
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                                    <CheckCircle className="mx-auto text-emerald-500 mb-2" size={32} />
                                    <p className="text-emerald-700 font-medium">Nu există duplicate!</p>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDuplicatesModal(false)}>
                            Închide
                        </Button>
                        {duplicates?.duplicate_count > 0 && (
                            <Button 
                                onClick={cleanDuplicates} 
                                disabled={cleaningDuplicates}
                                className="bg-red-500 hover:bg-red-600"
                            >
                                {cleaningDuplicates ? (
                                    <><Loader2 size={16} className="animate-spin mr-2" /> Curățare...</>
                                ) : (
                                    <><Trash2 size={16} className="mr-2" /> Șterge Duplicate</>
                                )}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BazaDateProduse;
